import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  indirizzoScarico,
  peso,
  useCaricaFile,
  useDocumento,
  useOpzioniDocumenti,
} from '../api/documenti.js';
import { Dato } from '../componenti/Dato.js';
import { ZonaFile } from '../componenti/ZonaFile.js';
import { data } from '../formati.js';
import { ModaleDocumento } from './ModaleDocumento.js';
import { t } from '../i18n/lingua.js';
import { SeApre } from '../componenti/SeApre.js';

/**
 * La scheda di un documento.
 *
 * Tre cose che il v2 non dice e che qui stanno in cima:
 *
 *  - **con che nome esce il file**, che per 9.532 documenti non è quello
 *    scritto a database;
 *  - **se il file c'è davvero**: la dimensione arriva dall'archivio, e un
 *    allegato sparito si vede adesso invece che quando serve;
 *  - **chi altro usa quel file**: la creazione multipla ne fa uno per venti
 *    documenti, e sostituirlo da qui li cambia tutti.
 */
export function Documento({ id }: { id: number }) {
  const dati = useDocumento(id);
  const opzioni = useOpzioniDocumenti();
  const carica = useCaricaFile(id);
  const [modificaAperta, setModificaAperta] = useState(false);

  if (dati.isLoading) {
    return <div className="page-head">{t('Un momento…')}</div>;
  }
  if (dati.error || !dati.data) {
    return (
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Documento non trovato')}</h1>
          <Link className="back-link" to="/documents">
            {t('← Torna all’archivio')}
          </Link>
        </div>
      </div>
    );
  }

  const d = dati.data;
  const erroreCaricamento = carica.error instanceof ErroreApi ? carica.error.message : null;
  const mancante = d.fileName !== null && d.fileSize === null;

  return (
    <>
      <div className="page-head">
        <div>
          <Link className="back-link" to="/documents">
            {t('← Torna all’archivio')}
          </Link>
          <h1 className="page-title">{d.title}</h1>
          <div className="page-sub">
            {data(d.createdAt)}
            {d.protocolNumber && <> · {t('protocollo')} {d.protocolNumber}</>}
            {d.customer && (
              <>
                {' · '}
                <SeApre sezione="customers" altrimenti={d.customer}>
                  <Link className="cell-link" to="/customers/$id" params={{ id: String(d.identityId) }}>
                    {d.customer}
                  </Link>
                </SeApre>
              </>
            )}
            {d.archived && (
              <span className="badge" style={{ marginLeft: 8 }}>
                {t('archiviato')}
              </span>
            )}
          </div>
        </div>
        <div className="page-actions">
          {d.fileName && !mancante && (
            <a className="btn btn-primary" href={indirizzoScarico(d.id)} download={d.downloadName}>
              {t('Scarica')}
            </a>
          )}
          <button className="btn" onClick={() => setModificaAperta(true)}>
            {t('Modifica')}
          </button>
        </div>
      </div>

      <div className="grid grid-2" style={{ margin: '14px 20px 24px' }}>
        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Il file')}</span>
          </div>
          <div className="card-body">
            {erroreCaricamento && (
              <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
                <span className="avviso-segno">▲</span>
                <span>{erroreCaricamento}</span>
              </div>
            )}

            {mancante && (
              <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
                <span className="avviso-segno">▲</span>
                <span>
                  {t('Il documento dice di avere il file')} <span className="num">{d.fileName}</span>{t(', ma nell’archivio non c’è. Ricaricalo qui sotto.')}
                </span>
              </div>
            )}

            {d.fileName ? (
              <div className="fields">
                <Dato
                  etichetta="Esce con questo nome"
                  nota={
                    d.downloadName !== d.fileName
                      ? t(' — preso dal titolo, perché quello vero non dice niente')
                      : undefined
                  }
                  pieno
                >
                  {d.downloadName}
                </Dato>
                <Dato etichetta="Nome a archivio" mono>
                  {d.fileName}
                </Dato>
                <Dato etichetta="Dimensione" mono>
                  {d.fileSize === null ? null : peso(d.fileSize)}
                </Dato>
                <Dato etichetta="Formato">{d.fileFormat}</Dato>
                <Dato etichetta="Caricato il" mono>
                  {d.directory}
                </Dato>
              </div>
            ) : (
              <p className="dim" style={{ marginTop: 0, fontSize: 13 }}>
                {t('Questo documento non ha ancora un file.')}
              </p>
            )}

            {/* chi sta per sostituire un file condiviso deve saperlo prima */}
            {d.sharedWith > 0 && (
              <p className="nota-prezzo" style={{ marginTop: 12 }}>
                {d.sharedWith === 1
                  ? t('Questo file è anche di un altro documento: è stato caricato una volta sola per più ordini. Caricarne un altro qui lascia l’altro sul file di prima.')
                  : t('Questo file è anche di altri {n} documenti: è stato caricato una volta sola per più ordini. Caricarne un altro qui lascia gli altri sul file di prima.', {
                      n: d.sharedWith,
                    })}
              </p>
            )}

            <div style={{ marginTop: 14 }}>
              <ZonaFile
                file={null}
                estensioni={opzioni.data?.extensions}
                massimo={opzioni.data?.maxUploadBytes}
                onFile={(file) => file && carica.mutate(file)}
              />
              {carica.isPending && (
                <p className="dim" style={{ fontSize: 12, marginBottom: 0 }}>
                  {t('Sto caricando…')}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Dati del documento')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              <Dato etichetta="Titolo" pieno>
                {d.title}
              </Dato>
              <Dato etichetta="Numero di protocollo" mono>
                {d.protocolNumber}
              </Dato>
              <Dato etichetta="Cliente">
                {d.identityId ? (
                  <SeApre sezione="customers" altrimenti={d.customer}>
                    <Link className="cell-link" to="/customers/$id" params={{ id: String(d.identityId) }}>
                      {d.customer}
                    </Link>
                  </SeApre>
                ) : null}
              </Dato>
              <Dato etichetta="Ordine di vendita" pieno>
                {d.orderId ? (
                  <>
                    <SeApre sezione="sales-orders" altrimenti={d.orderNumber}>
                      <Link className="cell-link" to="/sales-orders/$id" params={{ id: String(d.orderId) }}>
                        {d.orderNumber}
                      </Link>
                    </SeApre>
                    {d.orderProtocol && (
                      <span className="dim num" style={{ marginLeft: 8 }}>
                        {d.orderSectional} {d.orderProtocol}
                      </span>
                    )}
                  </>
                ) : null}
              </Dato>
              <Dato etichetta="Categorie" pieno>
                {d.categories.length > 0 ? (
                  <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                    {d.categories.map((c) => (
                      <span className="badge" key={c.id}>
                        {c.name}
                      </span>
                    ))}
                  </span>
                ) : null}
              </Dato>
              <Dato etichetta="Parole chiave" pieno>
                {d.keywords}
              </Dato>
              <Dato etichetta="Descrizione" pieno>
                {d.description}
              </Dato>
              <Dato etichetta="Annotazioni" pieno>
                {d.notes}
              </Dato>
            </div>
          </div>
        </div>
      </div>

      {modificaAperta && (
        <ModaleDocumento documento={d} onChiudi={() => setModificaAperta(false)} />
      )}
    </>
  );
}
